/**
 * One floor's static geometry: slabs, walls, props, the rails and the set
 * dressing, markers and the item billboards, as instanced batches in one
 * group, plus its fog of war, and the Medium tier's parts (`medium-floor.ts`).
 * Built from a `RunState` whose `floor` is set; the seed decorates, it never
 * decides anything the simulation does. Shared geometries and materials
 * belong to the view, so a world's disposal frees only its own buffers.
 * `setTier` swaps materials and what is drawn (the prototype's `setQuality`);
 * it never rebuilds geometry. Fog of war: the ramp surfaces sample the
 * exploration mask in the shader at both tiers (smooth fog; the Off tier's
 * since slice 2b), while markers keep their per-instance fade.
 */
import type { ItemIcon, Theme } from '@ab/content-schema';
import type { Floor, Pos, RunState } from '@ab/game-core';
import * as THREE from 'three';
import type { PartBatch } from './batch.ts';
import { type Billboards, buildBillboards } from './billboards.ts';
import { presentation } from './constants.ts';
import { buildFloorParts } from './floor-geometry.ts';
import { createExplorationMask, reveal } from './fog.ts';
import { tileIndex } from './grid.ts';
import { type MarkerParts, buildMarkers } from './markers.ts';
import type { ShaderHook } from './material-hooks.ts';
import type { MediumKind } from './medium.ts';
import { type MediumFloor, buildMediumFloor } from './medium-floor.ts';
import type { Palette } from './palette.ts';
import { type PropParts, buildProps } from './props.ts';
import { buildRockMass } from './rock-mass.ts';

export type Tier = 'off' | 'medium';

export type Shared = {
  readonly box: THREE.BufferGeometry;
  readonly round: THREE.BufferGeometry;
  /** The dressing's shapes: loose rock, barrel staves, iron bands, tufts. */
  readonly pebble: THREE.BufferGeometry;
  readonly barrel: THREE.BufferGeometry;
  readonly band: THREE.BufferGeometry;
  readonly tuft: THREE.BufferGeometry;
  /** Off: unlit vertex colours with per-instance colours (markers, glows, water, tufts). */
  readonly solid: THREE.Material;
  /** Off: the same, faded by the exploration mask in the shader (the ramp surfaces). */
  readonly offFog: THREE.Material;
  /** Points the Off fog material at this floor's mask. */
  readonly bindOffFog: (mask: THREE.Texture, floor: Floor) => void;
  /** The Medium tier's materials by kind. */
  readonly medium: Readonly<Record<MediumKind, THREE.Material>>;
  /** The Medium tier's light and fog hook for the item billboards (their material is the floor's: it holds its atlas). */
  readonly billboardLight: ShaderHook;
};

export type World = {
  readonly floor: Floor;
  readonly group: THREE.Group;
  readonly markers: MarkerParts;
  readonly billboards: Billboards;
  readonly props: PropParts;
  readonly medium: MediumFloor;
  /** Tiles of the boss's dais: a unit standing there is lifted onto it. */
  readonly dais: ReadonlySet<number>;
  readonly seen: ReadonlySet<number>;
  isSeen(tile: number): boolean;
  /** Reveals around a tile and updates the fog at the current tier. */
  revealFrom(p: Pos): void;
  /** Marks tiles seen (a rebuild keeps what was explored). */
  restoreSeen(tiles: Iterable<number>): void;
  setTier(tier: Tier): void;
  dispose(): void;
};

/** A batch, its mesh, its Medium kind (null: drawn at Off only), whether it is a ramp surface (fogged in the shader) and whether it glows. */
type Entry = { readonly batch: PartBatch; readonly mesh: THREE.InstancedMesh; readonly kind: MediumKind | null; readonly ramp: boolean; readonly glow: boolean };

export const buildWorld = (state: RunState, seed: number, theme: Theme, palette: Palette, shared: Shared, tier: Tier, icons: Readonly<Record<string, ItemIcon>>): World => {
  const floor = state.floor;
  if (floor === null) throw new Error('the run has no floor to show (state.floor is null)');
  const base = buildFloorParts(floor, seed, theme, palette);
  const mass = buildRockMass(floor, seed, theme, palette.wall);
  const mask = createExplorationMask(floor);
  const props = buildProps(floor, seed, theme, palette, base.walls, state.units, mass.topAt);
  for (const p of props.bed) base.floor.add(p);
  const markers = buildMarkers(floor, seed, theme, palette, state.units);
  const billboards = buildBillboards(floor, theme, icons, shared.billboardLight);
  const group = new THREE.Group();
  group.name = 'dungeon-floor';
  const entries: Entry[] = [];
  const add = (batch: PartBatch, geometry: THREE.BufferGeometry, kind: Entry['kind'], ramp: boolean, glow = false): void => {
    const mesh = batch.build(geometry, shared.solid);
    if (mesh === null) return;
    group.add(mesh);
    entries.push({ batch, mesh, kind, ramp, glow });
  };
  // Kind null draws at Off only: slice 1's wall boxes, trim and rock shoulders, which the rock mass's own
  // shoulders and ledges replace at Medium (boxes standing on its low stubs read as crates).
  add(base.floor, shared.box, 'stone', true);
  add(base.wall, shared.box, null, true);
  add(base.trim, shared.box, null, true);
  add(props.timber, shared.box, 'timber', true);
  add(props.iron, shared.box, 'iron', true);
  add(props.rock, shared.box, null, true);
  add(props.shaped.rock, shared.pebble, 'boulder', true);
  add(props.shaped.timberRound, shared.barrel, 'timber', true);
  add(props.shaped.ironRound, shared.band, 'iron', true);
  add(props.shaped.foliage, shared.tuft, 'lit', true);
  add(markers.boxes, shared.box, 'lit', false);
  add(markers.rounds, shared.round, 'lit', false);
  add(markers.glows, shared.box, 'unlit', false, true);
  add(markers.water, shared.round, 'water', false);
  group.add(billboards.mesh);
  const medium = buildMediumFloor(floor, state.units, seed, theme, mass, mask, props, shared.medium.rockmass);
  group.add(medium.rock, medium.lighting.group);
  const seen = new Set<number>();
  const isSeen = (tile: number): boolean => seen.has(tile);
  const dais = new Set(state.units.filter((u) => u.boss !== null && !u.down).map((u) => tileIndex(floor, u.pos.x, u.pos.y)));
  let current: Tier = tier;
  // Markers fade toward the fog each tier draws: the Off palette's fog, or the Medium tier's fog hue.
  const mediumFog = new THREE.Color(theme.lightRules.fogHue);
  const recolor = (): void => {
    const { markerFade, markerDarken, glowDarken } = presentation.fog;
    const fog = current === 'medium' ? mediumFog : palette.fog;
    for (const e of entries) {
      // The ramp surfaces keep their tint: the fog is drawn from the exploration mask, at both tiers.
      if (e.ramp && e.kind !== 'lit') e.batch.recolor(() => true, palette.fog);
      else e.batch.recolor(isSeen, fog, markerFade, e.glow ? glowDarken : markerDarken);
    }
  };
  const setTier = (next: Tier): void => {
    current = next;
    const m = next === 'medium';
    for (const e of entries) {
      e.batch.setTier(m);
      e.mesh.visible = !m || e.kind !== null;
      e.mesh.material = m && e.kind !== null ? shared.medium[e.kind] : e.ramp && e.kind !== 'lit' ? shared.offFog : shared.solid;
      e.mesh.receiveShadow = m && !e.glow;
      // Markers cast into the shadow map at Medium (the prototype's solid and round marker batches on High and Balanced).
      e.mesh.castShadow = m && e.kind === 'lit' && !e.ramp;
    }
    billboards.setTier(m);
    medium.rock.visible = m;
    medium.lighting.group.visible = m;
    if (!m) {
      medium.release();
      shared.bindOffFog(mask.texture, floor);
    }
    recolor();
  };
  setTier(tier);
  return {
    floor,
    group,
    markers,
    billboards,
    props,
    medium,
    dais,
    seen,
    isSeen,
    revealFrom(p) {
      reveal(floor, seen, p);
      medium.updateFog(seen);
      recolor();
    },
    restoreSeen(tiles) {
      for (const t of tiles) seen.add(t);
      medium.updateFog(seen);
      recolor();
    },
    setTier,
    dispose() {
      for (const e of entries) e.batch.dispose();
      billboards.dispose();
      medium.dispose();
      group.removeFromParent();
    },
  };
};
